import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import AuthGuard from "../components/AuthGuard";
import { api, getToken } from "../api/client";
import { TestTube, MapPin, Clock, FileDown, CheckCircle2, Sparkles, Circle } from "lucide-react";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const STEPS = [
    { key: "confirmed", label: "Confirmed" },
    { key: "sample_collected", label: "Sample collected" },
    { key: "processing", label: "Processing" },
    { key: "report_ready", label: "Report ready" },
];

const stepIndex = (status) => {
    const i = STEPS.findIndex((s) => s.key === status);
    return i === -1 ? -1 : i;
};

const LabOrderDetail = () => {
    const { id } = useParams();
    const [lab, setLab] = useState(null);
    const [loading, setLoading] = useState(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        setLoading(true);
        api.get(`/lab-orders/${id}`).then((r) => setLab(r.data)).catch(() => setLab(null)).finally(() => setLoading(false));
    }, [id]);

    const downloadReport = async () => {
        const url = `${BACKEND_URL}/api/lab-orders/${id}/report`;
        const token = getToken();
        try {
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error("download_failed");
            const blob = await res.blob();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `${lab?.lab_no || "lab-report"}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (e) {
            // fall back to opening tab
            window.open(url, "_blank");
        }
    };

    if (loading) return (<div className="min-h-screen bg-white dark:bg-[#0B0B12]"><Navbar /><main className="mx-auto max-w-4xl px-6 py-12 text-sm text-zinc-500">Loading…</main></div>);
    if (!lab) return (<div className="min-h-screen bg-white dark:bg-[#0B0B12]"><Navbar /><main className="mx-auto max-w-4xl px-6 py-12 text-sm text-zinc-500">Not found. <Link to="/lab-orders" className="text-brand-700 hover:underline">My lab orders</Link></main></div>);

    const idx = stepIndex(lab.status);
    const isCancelled = lab.status === "cancelled" || lab.status === "pending_payment";
    const cobj = lab.collection || {};
    const addr = cobj.address;

    return (
        <AuthGuard>
            <div className="min-h-screen bg-white dark:bg-[#0B0B12]">
                <Navbar />
                <main className="mx-auto max-w-4xl px-6 py-10 lg:px-12" data-testid="lab-order-detail">
                    <Link to="/lab-orders" className="text-xs text-zinc-500 hover:underline">← My lab orders</Link>
                    <div className="mt-3 flex items-start justify-between gap-3">
                        <div>
                            <p className="label-uppercase text-zinc-500">Lab order</p>
                            <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50" data-testid="lab-order-no">{lab.lab_no}</h1>
                            <p className="mt-1 text-xs text-zinc-500">Placed {new Date(lab.created_at).toLocaleString()}</p>
                        </div>
                        {lab.status === "report_ready" && (
                            <button onClick={downloadReport} className="btn-brand !py-2 !px-4 !text-xs" data-testid="lab-order-download-report">
                                <FileDown className="mr-1 inline h-3.5 w-3.5" /> Download report
                            </button>
                        )}
                    </div>

                    {/* Progress tracker */}
                    {!isCancelled && (
                        <section className="mt-6 rounded-2xl border border-zinc-100 bg-white p-5 dark:bg-white/[0.03] dark:border-white/10" data-testid="lab-order-tracker">
                            <p className="label-uppercase text-zinc-500">Status</p>
                            <ol className="mt-4 flex flex-wrap gap-x-6 gap-y-3 sm:flex-nowrap sm:items-center">
                                {STEPS.map((s, i) => {
                                    const done = idx >= i;
                                    return (
                                        <li key={s.key} className="flex items-center gap-2 text-xs">
                                            {done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-zinc-300" />}
                                            <span className={done ? "font-medium text-zinc-900 dark:text-zinc-50" : "text-zinc-500"}>{s.label}</span>
                                            {i < STEPS.length - 1 && <span className="mx-1 hidden h-px w-8 bg-zinc-200 sm:inline-block" />}
                                        </li>
                                    );
                                })}
                            </ol>
                        </section>
                    )}

                    {isCancelled && (
                        <section className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800" data-testid="lab-order-status-banner">
                            {lab.status === "pending_payment" ? "Awaiting payment confirmation." : "This lab order was cancelled."}
                        </section>
                    )}

                    <div className="mt-6 grid gap-4 lg:grid-cols-3">
                        <section className="rounded-2xl border border-zinc-100 bg-white p-5 lg:col-span-2 dark:bg-white/[0.03] dark:border-white/10" data-testid="lab-order-items">
                            <p className="label-uppercase text-zinc-500">Tests ordered</p>
                            <ul className="mt-3 space-y-2">
                                {(lab.items || []).map((it, i) => (
                                    <li key={i} className="flex items-start justify-between gap-2 text-sm">
                                        <span className="flex items-start gap-2"><TestTube className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-700" />{it.test_name || it.package_name}</span>
                                        <span className="font-medium text-zinc-900 dark:text-zinc-50">{inr(it.selling_price)}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-4 space-y-1 border-t border-zinc-100 pt-3 text-xs dark:border-white/10">
                                <p className="flex justify-between text-zinc-500"><span>Subtotal</span><span>{inr(lab.subtotal || 0)}</span></p>
                                {Number(lab.elite_discount || 0) > 0 && (
                                    <p className="flex justify-between text-amber-700"><span className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3" /> Elite discount</span><span>− {inr(lab.elite_discount)}</span></p>
                                )}
                                <p className="flex justify-between border-t border-zinc-100 pt-2 text-sm font-semibold text-zinc-900 dark:border-white/10 dark:text-zinc-50"><span>Total paid</span><span>{inr(lab.total || 0)}</span></p>
                            </div>
                        </section>

                        <aside className="space-y-4">
                            <section className="rounded-2xl border border-zinc-100 bg-white p-4 dark:bg-white/[0.03] dark:border-white/10" data-testid="lab-order-collection">
                                <p className="label-uppercase text-zinc-500">Collection</p>
                                <p className="mt-2 text-xs text-zinc-700 dark:text-zinc-300">
                                    <Clock className="mr-1 inline h-3 w-3" />
                                    {cobj.scheduled_at ? new Date(cobj.scheduled_at).toLocaleString() : "—"}
                                </p>
                                {cobj.type === "home" && addr ? (
                                    <p className="mt-2 text-xs text-zinc-500"><MapPin className="mr-0.5 inline h-3 w-3" />{[addr.line1, addr.line2, addr.city, addr.state, addr.pincode].filter(Boolean).join(", ")}</p>
                                ) : (
                                    <p className="mt-2 text-xs text-zinc-500">Visit the lab.</p>
                                )}
                                {cobj.notes && <p className="mt-2 text-[11px] italic text-zinc-500">"{cobj.notes}"</p>}
                            </section>

                            <section className="rounded-2xl border border-zinc-100 bg-white p-4 dark:bg-white/[0.03] dark:border-white/10">
                                <p className="label-uppercase text-zinc-500">Patient</p>
                                <p className="mt-2 text-sm font-medium text-zinc-900 dark:text-zinc-50">{lab.patient_details?.full_name}</p>
                                <p className="text-xs text-zinc-500">{lab.patient_details?.age} yrs · {lab.patient_details?.gender}</p>
                                <p className="text-xs text-zinc-500">{lab.patient_details?.phone}</p>
                            </section>
                        </aside>
                    </div>
                </main>
                <Footer />
            </div>
        </AuthGuard>
    );
};

export default LabOrderDetail;
